import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/modal'
import {
  Button,
  Icon,
  Text,
  Box,
  Image,
  VStack,
  HStack,
  Input,
  Center,
  AspectRatio,
  Flex,
  useDisclosure,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { RiImageLine, RiUploadLine, RiDeleteBinLine } from 'react-icons/ri'
import React, { useState, useRef, useCallback, useContext } from 'react'
import useToast from '../../../../hooks/useToast'
import { MediaLibrarySearchCollectionResponse } from '../../../../interfaces'
import useFetch, { CachePolicies } from 'use-http'
import { MLContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import { colors } from '../../../../../ui-kit'

// 5 MB
const FileSizeLimit = 5 * 1024 * 1024

type ChangeThumbnailModalProps = {
  isOpen: boolean
  onClose(): void
  currentSelectedCollection: MediaLibrarySearchCollectionResponse
}

const VariantMap = {
  NO_IMAGE: 'no_image',
  PRESET_IMAGE: 'preset_image',
  NEW_IMAGE: 'new_image',
} as const

const UpdateThumbnailActions = {
  ADD: 'add',
  REMOVE: 'remove',
} as const

const ChangeThumbnailModal = ({
  isOpen,
  onClose,
  currentSelectedCollection,
}: ChangeThumbnailModalProps): React.ReactElement => {
  const [variant, setVariant] = useState<
    typeof VariantMap[keyof typeof VariantMap]
  >(
    currentSelectedCollection.collectionThumbnail
      ? VariantMap.PRESET_IMAGE
      : VariantMap.NO_IMAGE,
  )
  const [isLoading, setIsLoading] = useState(false)

  const [, dispatch] = useContext(MLContext)
  const { t } = useTranslation()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
    interceptors: {
      request: ({ options, url, path, route }) => {
        // For multipart requests, don't override headers - let browser set them
        if (options.body instanceof FormData) {
          // Remove any Content-Type header that might have been set globally
          const headers = { ...options.headers }
          delete headers['Content-Type']
          delete headers['content-type']
          return {
            ...options,
            headers,
          }
        }
        return options
      },
    },
  })

  const onSubmit = async (file: File) => {
    const formData = new FormData()
    formData.append('thumbnail', file)
    formData.append('fileName', file.name)
    formData.append('action', UpdateThumbnailActions.ADD)

    // Don't set Content-Type header manually for FormData

    const resp = await request.put(
      `/api/v1/media-collections/${currentSelectedCollection.mediaCollectionId}/thumbnail`,
      formData,
    )

    if (request.error) {
      toast({
        description: t('error_updating_thumbnail', 'Error updating thumbnail.'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return false
    } else {
      const patch = {
        ...currentSelectedCollection,
        collectionThumbnail: resp.thumbnailURL || undefined,
      }

      dispatch({
        type: MediaLibraryActions.UPDATE_MEDIA_COLLECTIONS,
        payload: [patch],
      })

      toast({
        description: t(
          'thumbnail_updated_successfully',
          'Thumbnail updated successfully.',
        ),
        status: 'success',
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
        isClosable: true,
      })
      return true
    }
  }

  const onRemoveThumbnail = async () => {
    const formData = new FormData()
    formData.append('action', UpdateThumbnailActions.REMOVE)
    setIsLoading(true)
    const resp = await request.put(
      `/api/v1/media-collections/${currentSelectedCollection.mediaCollectionId}/thumbnail`,
      formData,
    )
    setIsLoading(false)

    if (request.error) {
      toast({
        description: t('error_removing_thumbnail', 'Error removing thumbnail.'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      const patch = {
        ...currentSelectedCollection,
        collectionThumbnail: resp.thumbnailURL || undefined,
      }
      dispatch({
        type: MediaLibraryActions.UPDATE_MEDIA_COLLECTIONS,
        payload: [patch],
      })
      setVariant(VariantMap.NO_IMAGE)
      toast({
        description: t(
          'thumbnail_removed_successfully',
          'Thumbnail removed successfully.',
        ),
        status: 'success',
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
        isClosable: true,
      })
      onDeleteAlertClose()
      handleClose()
    }
  }

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          description: t(
            'invalid_file_type_image',
            'Please select a valid image file.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      if (file.size > FileSizeLimit) {
        toast({
          description: t('file_size_too_large', 'File size too large.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      setSelectedFile(file)
      setVariant(VariantMap.NEW_IMAGE)
      // Create preview URL
      const reader = new FileReader()
      reader.onload = e => {
        setPreviewUrl(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    },
    [toast, t],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
  }, [])


  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const file = event.dataTransfer.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        toast({
          description: t(
            'invalid_file_type_image',
            'Please select a valid image file.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      if (file.size > FileSizeLimit) {
        toast({
          description: t('file_size_too_large', 'File size too large.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      setSelectedFile(file)
      setVariant(VariantMap.NEW_IMAGE)

      const reader = new FileReader()
      reader.onload = e => {
        setPreviewUrl(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    },
    [toast, t],
  )

  const handleSubmit = useCallback(async () => {
    if (!selectedFile || !onSubmit) return
    // else upload the new file
    setIsLoading(true)
    const success = await onSubmit(selectedFile)
    if (success) {
      handleClose()
    }
    setIsLoading(false)
  }, [selectedFile, onSubmit])

  const resetFile = () => {
    setPreviewUrl(null)
    setSelectedFile(null)
    setVariant(VariantMap.NO_IMAGE)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = useCallback(() => {
    resetFile()
    setIsLoading(false)
    onClose()
  }, [onClose])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
    // Clear current selection when changing file
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex alignItems="center">
            <Icon
              as={RiImageLine}
              color={colors.appBattleshipGrey}
              backgroundColor={colors.appPaleGrey}
              boxSize={6}
              padding="0.1em"
              borderRadius="full"
              marginRight="0.5em"
            />
            {t('change_thumbnail', 'Change thumbnail')}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="20px">
          <VStack spacing={5} align="stretch">
            {/* Single preview section */}
            <Box>
              <Center>
                {/* Invisible input to select file */}
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  display="none"
                />
                <AspectRatio ratio={1} width="100%">
                  {variant === VariantMap.PRESET_IMAGE ||
                  variant === VariantMap.NEW_IMAGE ? (
                    <Image
                      // objectFit is not working in the image and is being overridden by AspectRatio, so we are using style to set it
                      style={{
                        objectFit: 'contain',
                      }}
                      src={
                        variant === VariantMap.PRESET_IMAGE
                          ? `${currentSelectedCollection.collectionThumbnail}?tr=w-800,h-800,cm-pad_resize,fo-center`
                          : previewUrl || ''
                      }
                      alt="Current thumbnail"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="gray.200"
                    />
                  ) : (
                    <Box
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="8px"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={openFilePicker}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={4}
                    >
                      <VStack spacing={3}>
                        <Icon as={RiUploadLine} boxSize={8} color="gray.400" />
                        <VStack spacing={1}>
                          <Text fontSize="md" fontWeight="medium">
                            {t(
                              'upload_thumbnail_image',
                              'Upload thumbnail image',
                            )}
                          </Text>
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            textAlign="center"
                          >
                            {t(
                              'drag_drop_or_click',
                              'Drag and drop an image here, or click to select',
                            )}
                          </Text>
                        </VStack>
                      </VStack>
                    </Box>
                  )}
                </AspectRatio>
              </Center>
            </Box>
            <HStack spacing={6}>
              <>
                <Button
                  size="sm"
                  variant="link"
                  onClick={openFilePicker}
                  color={colors.appBlue}
                >
                  {t('select_thumbnail', 'Select image')}
                </Button>
              </>
              {variant === VariantMap.PRESET_IMAGE && (
                <>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={onDeleteAlertOpen}
                    color={colors.appRed}
                  >
                    {t('clear_thumbnail', 'Reset')}
                  </Button>
                </>
              )}
            </HStack>
          </VStack>
        </ModalBody>
        {variant === VariantMap.NEW_IMAGE && (
          <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
            <Button onClick={handleClose} variant="outline">
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              colorScheme="appBlue"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText={t('uploading_thumbnail', 'Uploading...')}
            >
              {t('save', 'Save')}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>

      {/* Confirmation Dialog for Thumbnail Removal */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              <Flex alignItems="center">
                <Icon
                  as={RiDeleteBinLine}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('remove_thumbnail', 'Remove thumbnail')}
              </Flex>
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text color="appBattleshipGrey">
                {t(
                  'remove_thumbnail_confirmation',
                  'Are you sure you want to remove the current thumbnail? This action cannot be undone.',
                )}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter
              style={{ gap: '1em', borderTop: '1px solid #e7eaf0' }}
            >
              <Button
                ref={cancelRef}
                onClick={onDeleteAlertClose}
                disabled={isLoading}
                variant="outline"
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                colorScheme={colors.appRed}
                onClick={onRemoveThumbnail}
                isLoading={isLoading}
                loadingText={t('removing', 'Removing...')}
              >
                {t('remove', 'Remove')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  )
}

export default ChangeThumbnailModal
